/**
 * SegmentTimeline.jsx — Track B, Phase 1 (isolated).
 *
 * Reads GET /api/segments/distribution and renders an iOS-style
 * horizontal timeline of segments, ordered by priority.
 *
 * ISOLATION: This component is NOT mounted anywhere yet. It will
 * be attached to a preview route in Phase 3 for visual review.
 * Do not import it from existing customer screens.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import MetricExplainer from "./MetricExplainer";
import { InfoTip } from "../components/InfoTip";
import { getSegmentMeta } from "../lib/segmentation";
import { useSegmentsStatus } from "../lib/useSegmentsStatus";
import CustomerListModal from "../components/CustomerListModal";

// A3 (2026-10-12) — الترتيب البصري فقط محلي (مفقود أولاً عمداً لإبرازه
// كأولوية عمل)؛ كل تسمية/إيموجي/لون من تعريفات الخادم عبر getSegmentMeta
// — لا META محلي (كان مطابقاً لـbackend/services/segments.py يدوياً قبل
// اليوم، عرضة للانحراف عند أي تعديل إداري للتعريفات).
const SEGMENT_ORDER = [
  "lost", "gold_vip", "rising_star", "loyal", "at_risk",
  "promising", "new", "regular", "dormant", "drifter",
];

export default function SegmentTimeline({ branchId = null, windowDays = 90 }) {
  useSegmentsStatus(); // إعادة رسم بعد اكتمال تحميل تعريفات الشرائح
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [explain, setExplain] = useState({ open: false, code: null });
  // B1 (2026-10) — الرقم نفسه (segment-count) قابل للضغط ويفتح القائمة
  // الموحّدة؛ ⓘ يبقى مخصصاً للشرح فقط (onClick منفصل، لا تداخل).
  const [listModal, setListModal] = useState(null); // {code, label}

  // Phase 7c: نافذة تحليلية ثابتة (window_days) — غير موصولة بتاريخ الشريط
  // المشترك (المسار 1a). التوزيع/حدود RFM لا تُقلَّص بنافذة ضيقة.
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    const params = { window_days: windowDays };
    if (branchId) params.branch_id = branchId;

    api
      .get("/segments/distribution", { params })
      .then((r) => { if (!cancelled) setState({ loading: false, error: null, data: r.data }); })
      .catch((e) => {
        if (!cancelled) {
          const msg = e?.response?.data?.detail || e?.message || "خطأ";
          setState({ loading: false, error: msg, data: null });
        }
      });

    return () => { cancelled = true; };
  }, [branchId, windowDays]);

  const rows = useMemo(() => {
    const counts = state.data?.counts || {};
    const pct = state.data?.percentages || {};
    return SEGMENT_ORDER.map((code) => {
      const meta = getSegmentMeta(code);
      return {
        code,
        label: meta.label.replace(/^\S+\s*/, "") || code,
        emoji: meta.label.split(" ")[0] || "",
        color: meta.color,
        count: Number(counts[code] || 0),
        percentage: Number(pct[code] || 0),
        // تصحيح A3 (البند 1) — تعريف الشريحة الحقيقي من الخادم
        // (description_ar عبر getSegmentMeta)، بدل نص تعليمة استخدام ثابت.
        desc: meta.desc || "",
      };
    });
  }, [state.data]);

  const total = state.data?.total_customers || 0;
  const maxCount = Math.max(1, ...rows.map((r) => r.count));

  return (
    <div
      dir="rtl"
      data-testid="segment-timeline-root"
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', 'Noto Naskh Arabic', sans-serif",
        background: "#FFFFFF",
        borderRadius: 20,
        padding: "28px 24px",
        color: "#3D2A1C",
        boxShadow: "0 8px 28px rgba(61,42,28,0.10)",
        border: "1px solid rgba(61,42,28,0.12)",
        maxWidth: 720,
      }}
    >
      <header
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5 }}>
            الخريطة الزمنية للشرائح
          </div>
          <div style={{ fontSize: 13, color: "#78716C", marginTop: 4 }}>
            نافذة {windowDays} يوم{branchId ? ` · فرع ${branchId}` : " · كل الفروع"}
          </div>
          {/* v121.2 — Source transparency: explain WHAT this panel counts and
              why it may differ from the coach's weekly report. */}
          <div
            data-testid="segment-timeline-source"
            style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}
          >
            <span style={{
              fontSize: 10, padding: "2px 8px", borderRadius: 999,
              background: "#DCFCE7", color: "#15803D", fontWeight: 700,
            }}>
              🟢 المصدر: RETM الحيّة
            </span>
            <span style={{
              fontSize: 10, padding: "2px 8px", borderRadius: 999,
              background: "#E0E7FF", color: "#4338CA", fontWeight: 700,
            }}>
              📅 مرجع: {state.data?.ref_date || "اليوم"}
            </span>
            <span
              style={{
                fontSize: 10, padding: "2px 8px", borderRadius: 999,
                background: "#FEF3C7", color: "#92400E", fontWeight: 700, cursor: "help",
              }}
            ><InfoTip text={
                "هذه الشاشة تعدّ العملاء النشطين في آخر " + windowDays +
                " يوم من فواتير RETM الحيّة فقط. " +
                "قد ترى شرائح مثل «مفقود» و«واعد» = 0 هنا " +
                "بينما يعرضها تقرير coach الأسبوعي بأعداد كبيرة، " +
                "لأنّ التقرير يشمل بيانات Excel المرفوعة (تاريخ أطول) " +
                "ويقيس الحركة بين الأسابيع، لا لقطة النافذة الحالية."
              } />
              ℹ️ لماذا الأرقام تختلف عن coach؟
            </span>
          </div>
        </div>
        <div
          data-testid="segment-timeline-total"
          style={{
            background: "rgba(61,42,28,0.06)",
            borderRadius: 14,
            padding: "10px 16px",
            textAlign: "center",
            minWidth: 96,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, color: "#3D2A1C" }}>{total.toLocaleString("en-US")}</div>
          <div style={{ fontSize: 11, color: "#78716C", marginTop: 2 }}>عميل</div>
          <div data-testid="segment-timeline-clarifier"
               style={{ fontSize: 10, color: "#B45309", fontWeight: 700, marginTop: 6,
                        lineHeight: 1.5, maxWidth: 190, textAlign: "center" }}>
            ℹ️ حدود RFM من 90 يوم · recency من كل التاريخ — لهذا يختلف عن الرقم الرسمي (مجتمع القياس — 90 يوم)
          </div>
          <div data-testid="segment-timeline-vs-movement-note"
               style={{ fontSize: 9.5, color: "#A8A29E", marginTop: 4, maxWidth: 190, lineHeight: 1.5 }}>
            لقطة لحظية واحدة (لا تقاطع لقطتين) — أكبر عادة من «مجتمع القياس» في خريطة الحركة الأسبوعية.
          </div>
        </div>
      </header>

      {state.loading && (
        <div
          data-testid="segment-timeline-loading"
          style={{ padding: "40px 0", textAlign: "center", color: "#78716C" }}
        >
          جارٍ تحميل التوزيع…
        </div>
      )}

      {state.error && !state.loading && (
        <div
          data-testid="segment-timeline-error"
          style={{
            padding: "20px", borderRadius: 14, background: "rgba(255,59,48,0.12)",
            color: "#FF453A", fontSize: 14,
          }}
        >
          تعذّر تحميل البيانات: {state.error}
        </div>
      )}

      {!state.loading && !state.error && (
        <ul
          data-testid="segment-timeline-list"
          style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}
        >
          {rows.map((r) => {
            const barWidth = Math.max(2, Math.round((r.count / maxCount) * 100));
            return (
              <li
                key={r.code}
                data-testid={`segment-row-${r.code}`}
                onClick={() => setExplain({ open: true, code: r.code })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setExplain({ open: true, code: r.code });
                  }
                }}
                aria-label={`${r.label} — اضغط للشرح`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr auto auto",
                  alignItems: "center",
                  gap: 14,
                  padding: "12px 14px",
                  background: "rgba(61,42,28,0.06)",
                  borderRadius: 14,
                  border: "1px solid rgba(61,42,28,0.12)",
                  transition: "background 200ms ease, transform 150ms ease",
                  cursor: "pointer",
                  outline: "none",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(61,42,28,0.10)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(61,42,28,0.06)"; }}
                onFocus={(e)     => { e.currentTarget.style.background = "rgba(61,42,28,0.10)"; }}
                onBlur={(e)      => { e.currentTarget.style.background = "rgba(61,42,28,0.06)"; }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: `${r.color}22`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20,
                  }}
                >
                  {r.emoji}
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{r.label}</span>
                    <span style={{ fontSize: 13, color: "#78716C" }}>
                      {r.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: 6, background: "rgba(61,42,28,0.12)",
                      borderRadius: 999, overflow: "hidden",
                    }}
                  >
                    <div
                      data-testid={`segment-bar-${r.code}`}
                      style={{
                        width: `${barWidth}%`, height: "100%",
                        background: `linear-gradient(90deg, ${r.color} 0%, ${r.color}CC 100%)`,
                        borderRadius: 999,
                        transition: "width 400ms cubic-bezier(0.4, 0.0, 0.2, 1)",
                      }}
                    />
                  </div>
                </div>
                <div
                  data-testid={`segment-count-${r.code}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`${r.label} — اضغط لعرض القائمة التفصيلية`}
                  onClick={(e) => { e.stopPropagation(); setListModal({ code: r.code, label: r.label }); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault(); e.stopPropagation();
                      setListModal({ code: r.code, label: r.label });
                    }
                  }}
                  style={{
                    fontSize: 17, fontWeight: 700, minWidth: 52, textAlign: "center",
                    cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted",
                    textUnderlineOffset: 3,
                  }}
                >
                  {r.count.toLocaleString("en-US")}
                </div>
                <div
                  aria-hidden
                  data-testid={`segment-explain-hint-${r.code}`}
 
                  style={{
                    fontSize: 13, fontWeight: 700,
                    width: 22, height: 22, borderRadius: 999,
                    background: "rgba(61,42,28,0.12)",
                    color: "#78716C",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                ><InfoTip
                    testId={`segment-explain-infotip-${r.code}`}
                    text={`${r.desc || r.label} — نافذة ${windowDays} يوم (RFM) · المصدر: core.customer_population`}
                  />?</div>
              </li>
            );
          })}
        </ul>
      )}

      <footer style={{ marginTop: 18, fontSize: 11, color: "#A8A29E", textAlign: "center" }}>
        اضغط أي شريحة لعرض تعريفها ومصدرها
      </footer>

      <MetricExplainer
        category="segments"
        metricId={explain.code}
        isOpen={explain.open}
        onClose={() => setExplain({ open: false, code: null })}
      />
      {listModal && (
        <CustomerListModal
          kind="segment"
          branchId={branchId}
          segmentCode={listModal.code}
          dateTo={state.data?.ref_date || null}
          windowDays={windowDays}
          title={`${listModal.label} — الخريطة الزمنية للشرائح`}
          onClose={() => setListModal(null)}
        />
      )}
    </div>
  );
}
